import { useEffect, useMemo, useRef, useState } from 'react';
import { endPlacement } from '@/utils/rtl';
import { Button, Drawer, Tooltip, theme } from 'antd';
import { SendOutlined, ClearOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/hooks/useLanguage';
import api from '@/services/api';
import { useBrandAssets } from '@/brand';
import { ModelSelector } from '@modules/conversations/components/ModelSelector';
import { streamAdvisor, type ChatMessage } from '../services/agentService';
import type { PricingPayload } from '../types';
import '@modules/conversations/components/MessageItem.css';
import '@modules/conversations/components/ModelSelector.css';
import type { TFunc } from '@/utils/tfunc';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Either an Advanced `SimulationParams` OR a simple-sim payload with `_kind:'simple'`.
   *  The backend advisor dispatches on `_kind` so both shapes are valid. */
  params: any;
  /** Matching breakdown — Advanced `CostBreakdown` OR a simple-sim response. */
  breakdown: any;
  pricing: PricingPayload;
}

const suggestions = (t: TFunc) => [
  t('costSimulator.suggest.whyHigh'),
  t('costSimulator.suggest.tenX'),
  t('costSimulator.suggest.switchHaiku'),
  t('costSimulator.suggest.budget500'),
];

export function CostAgentChat({ open, onClose, params, breakdown, pricing }: Props) {
  const { t, direction } = useLanguage();
  const { token } = theme.useToken();
  const { icon: brandIcon } = useBrandAssets();

  const advisorCfg = pricing.simulator.advisor;
  // advisor block always ships in the pricing payload (backend fails loudly
  // if missing) — no inline fallback copy of the default model here.
  const [model, setModel] = useState(advisorCfg.default_model);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [toolEvents, setToolEvents] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText, toolEvents]);

  useEffect(() => () => { cancelRef.current?.(); }, []);

  // Family catalog from /ai/models/ — users see FAMILY names in the picker
  // ('Sonnet', 'GPT Mini', ...), never concrete versions. NOTE: the option
  // VALUES stay the concrete ids from the pricing catalog — the advisor
  // backend (_resolve_model_string) maps the sent id to a provider via the
  // pricing block, so a family key is not a valid wire value here. Only the
  // LABELS are family-ized (matched on family `id` or `resolved_model`);
  // ids matching no family keep their pricing-catalog name.
  const [modelFamilies, setModelFamilies] = useState<
    Array<{ id: string; name: string; resolved_model?: string }>
  >([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/ai/models/');
        const list = (response.data.models || []).map((m: any) => ({
          id: m.id,
          name: m.name || m.id,
          resolved_model: m.resolved_model || undefined,
          version_ids: m.version_ids || undefined,
        }));
        if (!cancelled) setModelFamilies(list);
      } catch {
        // Non-fatal — picker falls back to the pricing-catalog names.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const familyNameByRef = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of modelFamilies) {
      m.set(f.id, f.name);
      if (f.resolved_model) m.set(f.resolved_model, f.name);
    }
    return m;
  }, [modelFamilies]);

  // Only advisor-eligible models from the JSON config. Values stay concrete
  // PRICING ids (the backend cost engine needs them); labels are family-ized.
  // When two pricing ids map to the SAME family, keep each option's own
  // catalog name as a suffix so they stay distinguishable.
  const eligible = new Set(advisorCfg?.available_models ?? []);
  const filtered = pricing.models.filter(m => eligible.size === 0 || eligible.has(m.id));
  const familyLabelCounts = new Map<string, number>();
  for (const m of filtered) {
    const fam = familyNameByRef.get(m.id);
    if (fam) familyLabelCounts.set(fam, (familyLabelCounts.get(fam) || 0) + 1);
  }
  const advisorModels = filtered.map(m => {
    const fam = familyNameByRef.get(m.id);
    if (!fam) return { id: m.id, name: m.name };
    const dup = (familyLabelCounts.get(fam) || 0) > 1;
    return { id: m.id, name: dup ? `${fam} · ${m.name}` : fam };
  });

  function send(textOverride?: string) {
    const text = (textOverride ?? input).trim();
    if (!text) return;

    // If currently streaming, cancel current and persist whatever partial
    // text was streamed so far as the assistant's reply, then start a new
    // user turn. This lets the user interrupt and keep going naturally.
    if (loading) {
      cancelRef.current?.();
      if (streamingText) {
        setMessages(curr => [...curr, { role: 'assistant', content: streamingText + ' [interrupted]' }]);
      }
      setStreamingText('');
      setToolEvents([]);
    }

    setInput('');
    setError(null);
    setStreamingText('');
    setToolEvents([]);
    const baseHistory = loading && streamingText
      ? [...messages, { role: 'assistant' as const, content: streamingText + ' [interrupted]' }]
      : messages;
    const newMessages: ChatMessage[] = [...baseHistory, { role: 'user', content: text }];
    setMessages(newMessages);
    setLoading(true);

    let buffer = '';
    cancelRef.current = streamAdvisor(newMessages, params, breakdown, model, (evt) => {
      if (evt.type === 'tool' && evt.name) {
        setToolEvents(prev => [...prev, evt.name!]);
      } else if (evt.type === 'delta' && evt.text) {
        buffer += evt.text;
        setStreamingText(buffer);
      } else if (evt.type === 'error') {
        setError(evt.error || t('common.unknownError'));
        setLoading(false);
      } else if (evt.type === 'done') {
        if (buffer) {
          setMessages(curr => [...curr, { role: 'assistant', content: buffer }]);
        }
        setStreamingText('');
        setToolEvents([]);
        setLoading(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    });
  }

  function handleClear() {
    cancelRef.current?.();
    setMessages([]);
    setStreamingText('');
    setToolEvents([]);
    setError(null);
    setLoading(false);
  }

  const Avatar = () => (
    <div className="message-avatar" style={{ width: 32, height: 32 }}>
      {brandIcon
        ? <img src={brandIcon} alt="" className="avatar-logo" />
        : <span style={{ fontSize: 14, fontWeight: 700 }}>AI</span>}
    </div>
  );

  return (
    <Drawer
      placement={endPlacement(direction)}
      open={open}
      onClose={onClose}
      width={520}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--agent-soft, rgba(108, 199, 239, 0.12))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {brandIcon ? <img src={brandIcon} alt="" style={{ width: '70%', height: '70%', objectFit: 'contain' }} /> :
              <span style={{ fontSize: 13, fontWeight: 700 }}>AI</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{t('costSimulator.agentChat.title')}</div>
            <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}>
              {t('costSimulator.agentChat.subtitle')}
            </div>
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {/* Reuse the chat module's ModelSelector — sparkle icon trigger + popover.
              The chat module's popover opens UPWARD (suited to a chat input at the
              page bottom); in this Drawer header we flip it to open DOWNWARD via
              the .advisor-model-switcher class override below. */}
          <span className="advisor-model-switcher">
            <ModelSelector
              models={advisorModels}
              selectedModel={model}
              onModelChange={setModel}
            />
          </span>
          <Tooltip title={t('costSimulator.agentChat.aboutTooltip')}>
            <Button size="small" type="text" icon={<InfoCircleOutlined />} onClick={() => setShowInfo(s => !s)} />
          </Tooltip>
          {messages.length > 0 && (
            <Tooltip title={t('costSimulator.agentChat.newConversation')}>
              <Button size="small" type="text" icon={<ClearOutlined />} onClick={handleClear} />
            </Tooltip>
          )}
        </div>
      }
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%', background: token.colorBgLayout },
        header: { borderBottom: `1px solid ${token.colorBorderSecondary}` },
      }}
    >
      {/* Optional info panel (still here, just no big always-visible block) */}
      {showInfo && (
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${token.colorBorderSecondary}`, background: token.colorBgContainer }}>
          <div style={{ padding: 10, background: token.colorFillAlter, borderRadius: token.borderRadius, fontSize: 11.5, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, marginBlockEnd: 4 }}>{t('costSimulator.agentChat.aboutTitle')}</div>
            <div style={{ marginBlockEnd: 6 }}><strong>{t('costSimulator.agentChat.howCalcLabel')}:</strong> {t('costSimulator.agentChat.howCalc')}</div>
            <div style={{ marginBlockEnd: 6 }}><strong>{t('costSimulator.agentChat.toolsLabel')}:</strong> {t('costSimulator.agentChat.tools')}</div>
            <div style={{ marginBlockEnd: 6 }}><strong>{t('costSimulator.agentChat.contextLabel')}:</strong> {t('costSimulator.agentChat.context')}</div>
            <div style={{ opacity: 0.7 }}><em>{t('costSimulator.agentChat.noWeb')}</em></div>
          </div>
        </div>
      )}

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 8px' }}>
        {messages.length === 0 && !loading && (
          <div style={{ padding: '24px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <Avatar />
              <div style={{ flex: 1, fontSize: 14, color: token.colorText, lineHeight: 1.6 }}>
                {t('costSimulator.agentChat.greeting')}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginInlineStart: 42 }}>
              {suggestions(t).map(s => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  style={{
                    textAlign: 'start',
                    padding: '8px 12px',
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    fontSize: 12.5, color: token.colorText, cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`message-group ${m.role}`} style={{ marginBottom: 18 }}>
            {m.role === 'assistant' && <Avatar />}
            <div className="message-content">
              <div className="message-text cost-advisor-md" style={{ fontSize: 14, lineHeight: 1.65 }}>
                {m.role === 'assistant'
                  ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  : m.content}
              </div>
            </div>
          </div>
        ))}

        {/* Active streaming reply */}
        {loading && (streamingText || toolEvents.length > 0) && (
          <div className="message-group assistant" style={{ marginBottom: 18 }}>
            <Avatar />
            <div className="message-content">
              {toolEvents.length > 0 && (
                <div style={{ fontSize: 11, opacity: 0.6, marginBlockEnd: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {toolEvents.map((tn, i) => (
                    <div key={i}>⚙ {t('costSimulator.agentChat.calling', { tool: tn })}</div>
                  ))}
                </div>
              )}
              {streamingText && (
                <div className="message-text streaming cost-advisor-md" style={{ fontSize: 14, lineHeight: 1.65 }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
                  <span style={{ display: 'inline-block', width: 8, height: 14, background: token.colorPrimary, marginInlineStart: 4, verticalAlign: 'middle', animation: 'cursorBlink 1s infinite' }} />
                </div>
              )}
            </div>
          </div>
        )}

        {loading && !streamingText && toolEvents.length === 0 && (
          <div className="message-group assistant" style={{ marginBottom: 18 }}>
            <Avatar />
            <div className="message-content">
              <div style={{ display: 'flex', gap: 6, padding: '8px 0', alignItems: 'center' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: token.colorPrimary, animation: 'pulse 1.4s infinite' }} />
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: token.colorPrimary, animation: 'pulse 1.4s infinite 0.2s' }} />
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: token.colorPrimary, animation: 'pulse 1.4s infinite 0.4s' }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: 12, marginBlockStart: 8, fontSize: 12, color: token.colorError, background: token.colorErrorBg, borderRadius: token.borderRadius, border: `1px solid ${token.colorErrorBorder}` }}>
            {t('costSimulator.agentChat.errorTitle')}: {error}
          </div>
        )}
      </div>

      {/* Input area — clean, rounded, placeholder properly aligned */}
      <div style={{ padding: 12, borderTop: `1px solid ${token.colorBorderSecondary}`, background: token.colorBgContainer }}>
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center',
          background: token.colorBgLayout,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 24,
          padding: '4px 4px 4px 16px',
        }}>
          <textarea dir="auto"
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={loading ? t('costSimulator.agentChat.typingWhileResponding') : t('costSimulator.agentChat.placeholder')}
            rows={1}
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 14, fontFamily: 'inherit', resize: 'none',
              maxHeight: 140, padding: '8px 0', color: token.colorText,
              lineHeight: '20px',
            }}
          />
          <Button
            type="primary"
            shape="circle"
            icon={<SendOutlined />}
            onClick={() => send()}
            disabled={!input.trim()}
            title={loading ? t('costSimulator.agentChat.queueHint') : t('costSimulator.agentChat.send')}
            size="middle"
          />
        </div>
      </div>

      {/* Markdown table styling — fit width of drawer, no horizontal scroll.
          Also flip the ModelSelector popover to open DOWNWARD when it lives in
          the Drawer header (default in the chat module is upward, which gets
          clipped here). */}
      <style>{`
        .advisor-model-switcher .model-selector-popover {
          bottom: auto !important;
          top: calc(100% + 8px) !important;
          left: auto !important;
          right: 0 !important;
          transform-origin: top right !important;
          background: var(--ant-color-bg-elevated, #ffffff) !important;
          color: var(--ant-color-text, #1f2937) !important;
          border: 1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.08)) !important;
          box-shadow:
            0 8px 28px rgba(0, 0, 0, 0.12),
            0 2px 6px rgba(0, 0, 0, 0.06) !important;
          z-index: 1500 !important;
        }
        .advisor-model-switcher .model-selector-popover-label,
        .advisor-model-switcher .model-option-name {
          color: var(--ant-color-text, #1f2937) !important;
        }
        .advisor-model-switcher .model-selector-option:hover {
          background: var(--ant-color-fill-tertiary, rgba(0,0,0,0.04)) !important;
        }
        .advisor-model-switcher .model-selector-option.selected {
          background: var(--ant-color-primary-bg, #e6f4ff) !important;
          color: var(--ant-color-primary, #1677ff) !important;
        }
        @keyframes pulse {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1); }
        }
        @keyframes cursorBlink {
          0%, 50% { opacity: 1; }
          50.1%, 100% { opacity: 0; }
        }
        .cost-advisor-md table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.6em 0;
          font-size: 12.5px;
          table-layout: auto;
          display: block;
          overflow-x: auto;
        }
        .cost-advisor-md th,
        .cost-advisor-md td {
          border: 1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.08));
          padding: 4px 8px;
          text-align: start;
          word-break: break-word;
        }
        .cost-advisor-md th {
          background: var(--ant-color-fill-alter, rgba(0,0,0,0.04));
          font-weight: 600;
        }
        .cost-advisor-md ul,
        .cost-advisor-md ol {
          padding-inline-start: 1.4em;
          margin: 0.4em 0;
        }
        .cost-advisor-md code {
          background: var(--ant-color-fill-alter, rgba(0,0,0,0.06));
          padding: 1px 4px;
          border-radius: 3px;
          font-size: 12px;
        }
      `}</style>
    </Drawer>
  );
}
